import React, { useState } from "react";
import { BUSINESS_TYPES } from "../constants";
import { validateSTRN, scoreSignals } from "../utils";
import { useFBRLookup } from "../hooks/useLookupNTN";
import { Disclaimer } from "../components/Disclaimer";
import { BusinessTypePicker } from "../components/BusinessTypePicker";
import { ResultCard } from "../components/ResultCard";

export function CheckPage({ t }) {
  const [businessType, setBusinessType] = useState("restaurant");
  const [ntn,    setNtn]    = useState("");
  const [strn,   setStrn]   = useState("");
  const [sntn,   setSntn]   = useState("");
  const [result, setResult] = useState(null);
  const { lookupNTN } = useFBRLookup();

  const ntnLookup = ntn.trim()  ? lookupNTN(ntn)   : null;
  const strnV     = strn.trim() ? validateSTRN(strn) : null;
  // SNTN treated as NTN lookup
  const sntnLookup = sntn.trim() ? lookupNTN(sntn) : null;
  const canCheck   = ntn.trim() || strn.trim() || sntn.trim();

  const handleCheck = () => {
    if (!canCheck) return;
    const effectiveLookup = sntnLookup || ntnLookup;
    setResult(scoreSignals({ ntnLookup: effectiveLookup, strnResult: strnV, hasQR: false, qrAnalysis: null, hasTaxClaimed: true, ocrNTN: null, businessType, lookupNTN}));
  };

  const bt = BUSINESS_TYPES.find(b => b.id === businessType);

  return (
    <div className="container">
      <div className="page-header">
        <h2 className="page-title">{t("nav_verify")}</h2>
        <p className="page-sub">{t("verify_sub")}</p>
      </div>
      <Disclaimer t={t} />

      <div className="form-card"><BusinessTypePicker value={businessType} onChange={nt => { setBusinessType(nt); setResult(null); }} t={t} /></div>

      <div className="form-card">
        {/* NTN */}
        <div className="form-group">
          <label className="field-label">{t("ntn_label")} <span className="field-tag">FBR Federal</span></label>
          <div className="input-wrap">
            <input className={`field ${ntnLookup ? (ntnLookup.found ? "field-ok" : "field-err") : ""}`}
              value={ntn} onChange={e => { setNtn(e.target.value); setResult(null); }}
              placeholder="e.g. 7221683-2" maxLength={12} />
            {ntnLookup && <span className={`field-icon ${ntnLookup.found ? "ok" : "err"}`}>{ntnLookup.found ? "✓" : "✗"}</span>}
          </div>
          {ntnLookup && <div className={`field-msg ${ntnLookup.found ? "field-msg-ok" : "field-msg-err"}`}>
            {ntnLookup.found ? `Found: ${ntnLookup.record.bn || ntnLookup.record.nm}${ntnLookup.checkMismatch ? " (check digit mismatch — printing error)" : ""}` : "Not found in FBR Active Taxpayer List"}
          </div>}
        </div>

        <div className="divider-or"><span>{t("and_or")}</span></div>

        {/* SNTN */}
        <div className="form-group">
          <label className="field-label">{t("sntn_label")} <span className="field-tag field-tag-srb">SRB Sindh</span></label>
          <div className="sntn-explainer-inline">{t("sntn_hint")}</div>
          <div className="input-wrap" style={{ marginTop:8 }}>
            <input className={`field ${sntnLookup ? (sntnLookup.found ? "field-ok" : "field-err") : ""}`}
              value={sntn} onChange={e => { setSntn(e.target.value); setResult(null); }}
              placeholder="e.g. S7221683-2" maxLength={13} />
            {sntnLookup && <span className={`field-icon ${sntnLookup.found ? "ok" : "err"}`}>{sntnLookup.found ? "✓" : "✗"}</span>}
          </div>
          {sntnLookup && <div className={`field-msg ${sntnLookup.found ? "field-msg-ok" : "field-msg-err"}`}>
            {sntnLookup.found ? `Found: ${sntnLookup.record.bn || sntnLookup.record.nm}${sntnLookup.checkMismatch ? " (check digit mismatch — printing error)" : ""}` : "Not found in FBR Active Taxpayer List"}
          </div>}
        </div>

        <div className="divider-or"><span>{t("and_or")}</span></div>

        {/* STRN */}
        <div className="form-group">
          <label className="field-label">STRN / Other Registration <span className="field-tag">FBR / PRA / KPRA / BRA</span></label>
          <div className="input-wrap">
            <input className={`field ${strnV ? (strnV.valid ? "field-ok" : "field-err") : ""}`}
              value={strn} onChange={e => { setStrn(e.target.value); setResult(null); }}
              placeholder="e.g. 9876543210123 · PRAxxxxxxx · KPRAxxxxxx" maxLength={20} />
            {strnV && <span className={`field-icon ${strnV.valid ? "ok" : "err"}`}>{strnV.valid ? "✓" : "✗"}</span>}
          </div>
          {strnV && <div className={`field-msg ${strnV.valid ? "field-msg-ok" : "field-msg-err"}`}>{strnV.reason}</div>}
        </div>

        <button className="btn btn-primary btn-full" onClick={handleCheck} disabled={!canCheck}>Validate & Get Verification Link</button>
      </div>

      {result && <ResultCard result={result} ntn={ntn.trim()||null} strn={sntn.trim()||strn.trim()||null} qrAnalysis={null} isSRB={!!sntn.trim()} t={t} />}

      {bt && (
        <div className="info-card" style={{ marginTop:20 }}>
          <div className="section-title" style={{ fontSize:14 }}>{bt.icon} {t(bt.label)} — Tax Guidance</div>
          <div className="check-list">
            <div className="check-item check-neutral">🏛️ Authority: <strong>{t(bt.authority)}</strong></div>
            <div className="check-item check-neutral">📊 Typical rate: <strong>{t(bt.rates)}</strong></div>
            <div className="check-item check-neutral" style={{ lineHeight:1.6 }}>ℹ️ {t(bt.taxNote)}</div>
          </div>
        </div>
      )}

      <div className="info-card" style={{ marginTop:20 }}>
        <div className="section-title" style={{ fontSize:14, marginBottom:12 }}>Registration Numbers — Quick Reference</div>
        <div className="reg-ref-grid">
          {[
            { code:"NTN",  full:"National Tax Number",         auth:"FBR",  eg:"7221683-2",    note:"All taxpayers. Looked up in FBR database." },
            { code:"SNTN", full:"Sindh Sales Tax Reg. Number", auth:"SRB",  eg:"S7221683-2",   note:"Sindh service providers. S prefix auto-stripped for FBR lookup." },
            { code:"STRN", full:"Sales Tax Registration",      auth:"FBR",  eg:"9876543210123", note:"Goods sellers, 13 digits." },
            { code:"PRA",  full:"Punjab Revenue Authority",    auth:"PRA",  eg:"PRA1234567890", note:"Punjab service providers." },
            { code:"KPRA", full:"KPK Revenue Authority",       auth:"KPRA", eg:"KPRA12345678",  note:"KPK service providers." },
            { code:"BRA",  full:"Balochistan Revenue Auth.",   auth:"BRA",  eg:"BRA12345678",   note:"Balochistan service providers." },
          ].map(r => (
            <div key={r.code} className="reg-ref-card">
              <div className="reg-ref-code">{r.code}</div>
              <div className="reg-ref-full">{r.full}</div>
              <div className="reg-ref-auth">{r.auth}</div>
              <div className="reg-ref-fmt"><code>{r.eg}</code></div>
              <div className="reg-ref-note">{r.note}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
